import { makeArticle, saintQuestionRelated } from "./learnFactory";

export const pageSaintOutpatientHospital = makeArticle({
  slug: "is-saint-tms-outpatient-or-hospital",
  title: "Is SAINT® TMS Outpatient or Inpatient? | Neuro Wellness",
  description:
    "SAINT® TMS is an outpatient treatment — no hospitalization required. Learn about the outpatient setting, why hospitalization is not needed, and what the treatment environment is like.",
  canonical: "/learn/is-saint-tms-outpatient-or-hospital",
  h1: "Is SAINT® TMS Outpatient or Inpatient?",
  breadcrumbLabel: "Is SAINT® Outpatient or Hospital?",
  shortAnswer:
    "SAINT® TMS is an outpatient treatment. No hospitalization is required. Patients come to the clinic each day for the five treatment days, receive their ten sessions, and leave when the day is complete. SAINT® does not use anesthesia or sedation, so there is no medical reason for an overnight stay. Neuro Wellness TMS Centers of America delivers SAINT® in a private outpatient setting in Coral Springs, Florida.",
  keyTakeaways: [
    "SAINT® is an outpatient treatment — no hospitalization required.",
    "Patients come to the clinic daily and leave when the day is complete.",
    "No anesthesia or sedation is used, so no overnight stay is needed.",
    "Neuro Wellness delivers SAINT® in a private outpatient setting.",
    "Patients traveling from out of town stay at a hotel, not a hospital.",
  ],
  sections: [
    {
      heading: "Why SAINT® Is Outpatient",
      paragraphs: [
        "SAINT® does not use anesthesia, sedation, or any procedure that requires medical monitoring after the treatment day ends. The magnetic pulses stimulate the brain non-invasively, and patients remain awake and alert throughout. Because there is no recovery from anesthesia, there is no medical reason for an overnight hospital stay.",
        "This makes SAINT® fundamentally different from ECT, which requires anesthesia and a recovery period, and from some inpatient psychiatric programs. SAINT® is delivered in a clinic, not an operating room or hospital ward.",
      ],
    },
    {
      heading: "The Outpatient Experience at Neuro Wellness",
      paragraphs: [
        "Neuro Wellness TMS Centers of America delivers SAINT® in a private outpatient setting at 9734 W Sample Rd, Coral Springs, FL 33065. Patients arrive in the morning, receive their ten sessions with breaks throughout the day, and leave when the day's schedule is complete. The environment is designed to be calm and comfortable, not clinical or institutional.",
        "Patients who travel from out of town typically stay at a nearby hotel for the week. The clinic can provide recommendations for accommodations. No hospital admission is involved at any point.",
      ],
      links: [{ to: "/travel-for-saint-tms", text: "See our travel guide" }],
    },
    {
      heading: "What About Monitoring?",
      paragraphs: [
        "While SAINT® is outpatient, the treatment team monitors patients throughout each day. The clinical staff checks on patients between sessions, asks about any side effects, and can adjust the treatment as needed. A psychiatrist oversees the treatment plan. The monitoring is for comfort and safety, not because the treatment requires medical stabilization.",
      ],
    },
  ],
  faqs: [
    {
      question: "Can I receive SAINT as an outpatient?",
      answer:
        "Yes. SAINT® is an outpatient treatment. You come to the clinic each day for the five treatment days, receive your sessions, and leave when the day is complete. No hospitalization is required at any point.",
    },
    {
      question: "Do I need to be hospitalized for SAINT?",
      answer:
        "No. SAINT® does not use anesthesia or sedation, so there is no medical reason for hospitalization. The treatment is delivered in an outpatient clinic. Patients who travel from out of town stay at a hotel, not a hospital.",
    },
    {
      question: "Does SAINT require anesthesia?",
      answer:
        "No. SAINT® is delivered while the patient is fully awake and alert. No anesthesia or sedation is used. This is one reason it can be delivered in an outpatient setting rather than a hospital.",
    },
    {
      question: "What is the treatment environment like?",
      answer:
        "Neuro Wellness delivers SAINT® in a private outpatient clinic in Coral Springs, Florida. The environment is calm and comfortable, designed for patients who spend most of the day at the clinic. It is not a hospital ward or operating room.",
    },
    {
      question: "Is SAINT safer because it is outpatient?",
      answer:
        "Being outpatient reflects the fact that SAINT® does not require anesthesia or medical stabilization, not that it is risk-free. All medical treatments carry some risk. SAINT®'s most common side effects are headache and scalp discomfort. Seizure is a rare but serious risk. Eligibility and safety are determined by a psychiatrist.",
    },
  ],
  sources: [],
  relatedLinks: saintQuestionRelated,
});
